import { COOKIE_SESSION_ID, COOKIE_TRANSACTION_ID, COOKIE_VISITOR_ID, STORAGE_KEY, VISITOR_ID_MAX_AGE } from './constants';
import type { StoredSession } from './types';

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

export function getCookie(name: string): string | null {
  for (const cookie of document.cookie.split('; ')) {
    const [cookieName, cookieValue] = cookie.split('=');
    if (cookieName === name) return decodeURIComponent(cookieValue);
  }
  return null;
}

export function setCookie(name: string, value: string, maxAgeSec: number, domain?: string): void {
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax; max-age=${maxAgeSec}`;
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

export function deleteCookie(name: string, domain?: string): void {
  setCookie(name, '', 0, domain);
}

// ---------------------------------------------------------------------------
// Session ID cookie
// ---------------------------------------------------------------------------

export function getSessionIdFromCookie(): string | null {
  return getCookie(COOKIE_SESSION_ID);
}

export function setSessionIdCookie(id: string, maxAgeSec: number, domain?: string): void {
  setCookie(COOKIE_SESSION_ID, id, maxAgeSec, domain);
}

// ---------------------------------------------------------------------------
// Transaction ID cookie
// ---------------------------------------------------------------------------

export function getTransactionIdFromCookie(): string | null {
  return getCookie(COOKIE_TRANSACTION_ID);
}

export function setTransactionIdCookie(value: string, maxAgeSec: number, domain?: string): void {
  setCookie(COOKIE_TRANSACTION_ID, value, maxAgeSec, domain);
}

// ---------------------------------------------------------------------------
// Visitor ID cookie (mf_vid) — per ADR-0015
// Normally server-set by CloudFront Function (first-party, 1yr TTL).
// JS fallback for non-owned domains (Safari ITP caps to 7 days).
// ---------------------------------------------------------------------------

export function getVisitorIdFromCookie(): string | null {
  return getCookie(COOKIE_VISITOR_ID);
}

export function setVisitorIdFallbackCookie(id: string, domain?: string): void {
  setCookie(COOKIE_VISITOR_ID, id, VISITOR_ID_MAX_AGE, domain);
}

// ---------------------------------------------------------------------------
// URL parameter helpers
// ---------------------------------------------------------------------------

export function getURLParameter(name: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) ?? undefined;
}

// ---------------------------------------------------------------------------
// localStorage — rich session state
// ---------------------------------------------------------------------------

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // localStorage full or unavailable — degrade silently
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
